import * as TableauUI from '@tableau/tableau-ui';
import * as React from 'react';
import { makeTestId } from '../../utils/Declarative';
import { PropsAndElementAttrs } from '../../utils/ReactHelpers';

export type BadgeKind = 'primary' | 'secondary' | 'outline';

export interface BadgeProps extends Pick<React.AriaAttributes, 'aria-label' | 'aria-labelledby'> {
  /**
   * If the surrounding context of the Badge will not be read correctly by a screen reader,
   * provide `aria-label` or `aria-labelledby` so the element will be accessible.
   */
  'aria-label'?: string;
  /**
   * If the surrounding context of the Badge will not be read correctly by a screen reader,
   * provide `aria-label` or `aria-labelledby` so the element will be accessible.
   */
  'aria-labelledby'?: string;
  /**
   * The content of the Badge.
   *
   * If `numeric` is `true` this value should only contain numerical characters (such as `0-9` and `?!#$%^*()-=+`).
  */
  children: string;
  /** Set to true when rendering on a dark background. This affects coloring of the Badge. */
  darkBackground?: boolean;
  /**
   * @default ```'primary'```
   * The color styling of the Badge to use.
   */
  kind?: BadgeKind;
  /** Numeric badges have more rounded corners and might be used e.g. to display notification badges for unread messages. */
  numeric?: boolean;
  /** Test id to be attached to the root element with suffix `-BadgeWidget`. */
  testId?: string;
}

/**
 * Badge is a static indicator that holds a small amount of information such as a condition, property, or some other metadata.
 *
 * All undocumented props will be passed to the root element.
 *
 * If a Badge is not surrounded by context-providing text, either `aria-label` or `aria-labelledby` must be provided for the element to be accessible.
 */
export const BadgeWidget: React.FC<PropsAndElementAttrs<BadgeProps>> = props => {
  const { testId, ...badgeProps } = props;

  return <TableauUI.Badge
    data-tb-test-id={makeTestId(testId, 'BadgeWidget')}
    {...badgeProps}
  />;
};

BadgeWidget.displayName = 'BadgeWidget';
